/**
 * Tagesverlauf Lesson Row Components
 *
 * LessonRow and related badge components for individual lessons.
 */

import React from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../../components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "../../../../components/ui/tooltip";
import {
  Clock,
  Check,
  X,
  ChevronDown,
  BookOpenText,
  Trash2,
  Info,
  AlertTriangle,
} from "lucide-react";
import { type TimelineLesson, getStatusInfo } from "./shared-hooks";
import {
  QuickStatusButtons,
  StatusDropdown,
  LatenessPopover,
} from "./TagesverlaufControls";

// ============================================================================
// CancelledLessonBadge - Badge for cancelled lessons
// ============================================================================

export interface CancelledLessonBadgeProps {
  lesson: TimelineLesson;
  label: string;
  setDeletingAttendanceLessonId: (id: string | null) => void;
  setShowDeleteAttendanceConfirm: (show: boolean) => void;
}

export function CancelledLessonBadge({
  lesson,
  label,
  setDeletingAttendanceLessonId,
  setShowDeleteAttendanceConfirm,
}: CancelledLessonBadgeProps) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs px-2 py-1 rounded bg-red-100 text-red-700">
        Entfällt
      </span>
      {lesson.status && lesson.attendanceRecordedByName && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                <button
                  type="button"
                  onClick={() => {
                    setDeletingAttendanceLessonId(lesson.lesson_id);
                    setShowDeleteAttendanceConfirm(true);
                  }}
                  className="p-1 rounded hover:bg-red-100 text-red-500 hover:text-red-700"
                  title="Anwesenheitsdaten löschen"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">
              <p className="text-sm font-medium text-amber-600">
                Anwesenheit auf entfallener Stunde
              </p>
              <p className="text-xs text-muted-foreground">
                Erfasst von: {lesson.attendanceRecordedByName}
                {lesson.attendanceRecordedAt && (
                  <>
                    {" "}
                    am{" "}
                    {new Date(lesson.attendanceRecordedAt).toLocaleString(
                      "de-DE",
                      {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      },
                    )}
                  </>
                )}
              </p>
              <p className="text-xs mt-1">Status: {lesson.status}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
}

// ============================================================================
// ExcursionLessonBadge - Badge for lessons during excursion
// ============================================================================

export interface ExcursionLessonBadgeProps {
  lesson: TimelineLesson;
  label: string;
  tripAttendanceStatus: string | null;
  savingTripAttendance: boolean;
  isFirstExcursionLesson: boolean;
  hasErroneousAttendance: boolean;
  isFutureDate?: boolean;
  handleTripAttendanceChange: (status: string) => void;
  handleDeleteExcursionLessonAttendance: (lessonId: string) => void;
}

export function ExcursionLessonBadge({
  lesson,
  label,
  tripAttendanceStatus,
  savingTripAttendance,
  isFirstExcursionLesson,
  hasErroneousAttendance,
  isFutureDate = false,
  handleTripAttendanceChange,
  handleDeleteExcursionLessonAttendance,
}: ExcursionLessonBadgeProps) {
  // Priority: local state (after user change) > attendance_log > daily_log (legacy fallback)
  const effectiveTripStatus = tripAttendanceStatus || lesson.status;
  const tripStatusLabel =
    effectiveTripStatus === "present"
      ? "Anwesend"
      : effectiveTripStatus === "late"
      ? "Verspätet"
      : effectiveTripStatus === "absent_excused"
      ? "Informiert"
      : effectiveTripStatus === "absent_unexcused"
      ? "Abwesend (U)"
      : effectiveTripStatus === "absent_unclear"
      ? "Ungeklärt (U)"
      : "Nicht erfasst";
  const tripStatusColor =
    effectiveTripStatus === "present"
      ? "bg-green-100 text-green-700 border-green-300"
      : effectiveTripStatus === "late"
      ? "bg-yellow-100 text-yellow-700 border-yellow-300"
      : effectiveTripStatus === "absent_excused"
      ? "bg-blue-100 text-blue-700 border-blue-300"
      : effectiveTripStatus === "absent_unexcused"
      ? "bg-red-100 text-red-700 border-red-300"
      : effectiveTripStatus === "absent_unclear"
      ? "bg-orange-100 text-orange-700 border-orange-300"
      : "bg-gray-100 text-gray-600 border-gray-300";

  if (isFirstExcursionLesson) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-xs px-2 py-1 rounded bg-amber-100 text-amber-700">
          Ausflug
        </span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              disabled={savingTripAttendance || isFutureDate}
              className={`text-xs px-2 py-1 rounded font-medium border flex items-center gap-1 ${tripStatusColor} ${
                savingTripAttendance || isFutureDate ? "opacity-50 cursor-not-allowed" : "hover:opacity-80"
              }`}
            >
              {tripStatusLabel}
              <ChevronDown className="h-3 w-3" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={() => handleTripAttendanceChange("present")}
            >
              <Check className="h-4 w-4 text-green-600 mr-2" /> Anwesend
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => handleTripAttendanceChange("late")}
            >
              <Clock className="h-4 w-4 text-yellow-600 mr-2" /> Verspätet
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => handleTripAttendanceChange("absent_excused")}
            >
              <X className="h-4 w-4 text-blue-600 mr-2" /> Informiert
            </DropdownMenuItem>
            {/* absent_unclear epic — Option B: trip-row "U" writes
                pre-klärung state; Prüfung is the only path to formal
                absent_unexcused. */}
            <DropdownMenuItem
              onClick={() => handleTripAttendanceChange("absent_unclear")}
            >
              <X className="h-4 w-4 text-orange-600 mr-2" /> Ungeklärt (U)
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        {hasErroneousAttendance && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() =>
                    lesson.lesson_id &&
                    handleDeleteExcursionLessonAttendance(lesson.lesson_id)
                  }
                  className="p-1 rounded hover:bg-red-100 text-red-500 hover:text-red-700"
                  title="Stundenanwesenheit löschen"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs">
                <p className="text-sm font-medium text-amber-600">
                  Stundenanwesenheit: {label}
                </p>
                <p className="text-xs text-muted-foreground">
                  Diese Stunde fand nicht statt (Ausflug). Klicken um den
                  Eintrag zu löschen.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    );
  }

  if (hasErroneousAttendance) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center gap-1">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              <span className="text-xs text-amber-600">{label}</span>
              <button
                type="button"
                onClick={() =>
                  lesson.lesson_id &&
                  handleDeleteExcursionLessonAttendance(lesson.lesson_id)
                }
                className="p-1 rounded hover:bg-red-100 text-red-500 hover:text-red-700"
                title="Anwesenheitsdaten löschen"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            <p className="text-sm font-medium text-amber-600">
              Anwesenheit während Ausflug
            </p>
            <p className="text-xs text-muted-foreground">
              Diese Stunde fand nicht statt (Ausflug). Anwesenheitseintrag kann
              gelöscht werden.
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return null;
}

// ============================================================================
// LessonRow - Individual lesson item in the timeline
// ============================================================================

export interface LessonRowProps {
  idx: number;
  lesson: TimelineLesson;
  lessons: TimelineLesson[];
  changes: Record<string, string>;
  latenessDetails: Record<string, { minutes: number; excused: boolean }>;
  setLatenessDetails: React.Dispatch<
    React.SetStateAction<Record<string, { minutes: number; excused: boolean }>>
  >;
  openLatenessPopover: string | null;
  setOpenLatenessPopover: (id: string | null) => void;
  tripAttendanceStatus: string | null;
  savingTripAttendance: boolean;
  isDebugMode: boolean;
  getEffectiveStatus: (
    lessonId: string | null,
    original: string | null,
  ) => string | null;
  setLessonStatus: (lessonId: string, status: string) => void;
  setLatenessInfo: (
    lessonId: string,
    minutes: number,
    excused: boolean,
  ) => void;
  handleTripAttendanceChange: (status: string) => void;
  handleDeleteExcursionLessonAttendance: (lessonId: string) => void;
  setDeletingAttendanceLessonId: (id: string | null) => void;
  setShowDeleteAttendanceConfirm: (show: boolean) => void;
  onOpenDiary?: (lessonId: string, isComplete: boolean) => void;
  isFutureDate?: boolean;
  /** Optional per-status label/colour overrides. Used by callers
   *  that want to re-skin the default STATUS_CONFIG entries for a
   *  specific dialog context (e.g. the Prüfung dialog renders
   *  absent_excused as "Informiert (Krankgemeldet)" / "Informiert (Beurlaubt)"
   *  instead of the plain "Informiert" when the admin has picked
   *  Krankgemeldet / Beurlaubt). */
  statusLabelOverrides?: Record<string, { label: string; color: string }>;
  /** Resolution-outcome label for this lesson, when one exists (e.g.
   *  "Ausnahmsweise als Gast in „Betreuung"" for a cancelled module the
   *  kid was reassigned from). Rendered inline under the lesson title. */
  resolutionLabel?: string;
}

export function LessonRow({
  idx,
  lesson,
  lessons,
  changes,
  latenessDetails,
  setLatenessDetails,
  openLatenessPopover,
  setOpenLatenessPopover,
  tripAttendanceStatus,
  savingTripAttendance,
  isDebugMode,
  getEffectiveStatus,
  setLessonStatus,
  setLatenessInfo,
  handleDeleteExcursionLessonAttendance,
  setDeletingAttendanceLessonId,
  setShowDeleteAttendanceConfirm,
  handleTripAttendanceChange,
  onOpenDiary,
  isFutureDate,
  statusLabelOverrides,
  resolutionLabel,
}: LessonRowProps) {
  const effectiveStatus = getEffectiveStatus(lesson.lesson_id, lesson.status);
  const lateInfo = lesson.lesson_id
    ? latenessDetails[lesson.lesson_id]
    : undefined;
  const defaultInfo = getStatusInfo(effectiveStatus, lateInfo);
  const override = effectiveStatus
    ? statusLabelOverrides?.[effectiveStatus]
    : undefined;
  const label = override?.label ?? defaultInfo.label;
  const color = override?.color ?? defaultInfo.color;
  const isChanged = lesson.lesson_id
    ? changes[lesson.lesson_id] !== undefined
    : false;
  const isNotEntered =
    !effectiveStatus || effectiveStatus === "nicht_eingetragen";
  const isCancelled = lesson.derivedCancelled === true;

  // Check if lesson is during class excursion
  const isOnExcursion = (() => {
    if (!lesson.classAbsenceId) return false;
    if (!lesson.classAbsenceStartTime || !lesson.classAbsenceEndTime)
      return true;
    return (
      lesson.start < lesson.classAbsenceEndTime &&
      lesson.end > lesson.classAbsenceStartTime
    );
  })();

  const isFirstExcursionLesson =
    isOnExcursion &&
    lessons.findIndex((l) => {
      if (!l.classAbsenceId) return false;
      if (!l.classAbsenceStartTime || !l.classAbsenceEndTime) return true;
      return (
        l.start < (l.classAbsenceEndTime || "") &&
        l.end > (l.classAbsenceStartTime || "")
      );
    }) === idx;

  const hasErroneousAttendance = !!(
    isOnExcursion &&
    lesson.status &&
    lesson.lesson_id
  );

  const rowClass = isCancelled
    ? "bg-red-50 rounded px-2 -mx-2"
    : isOnExcursion
    ? "bg-amber-50/50 rounded px-2 -mx-2"
    : "";

  return (
    <li className={`text-sm py-1 ${rowClass}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <span
              className={`font-medium ${
                isCancelled
                  ? "text-red-600"
                  : isOnExcursion
                  ? "text-amber-700"
                  : ""
              }`}
            >
              {(lesson.start ?? "").slice(0, 5)} –{" "}
              {(lesson.end ?? "").slice(0, 5)}
            </span>{" "}
            <span
              className={
                isCancelled
                  ? "text-red-600 line-through"
                  : isOnExcursion
                  ? "text-amber-700 line-through"
                  : isNotEntered
                  ? "text-red-600"
                  : ""
              }
            >
              {lesson.title}
            </span>
            {lesson.diaryEntryText && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-3.5 w-3.5 text-blue-500 cursor-help flex-shrink-0" />
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs">
                    <p className="text-sm">{lesson.diaryEntryText}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          {isCancelled && lesson.cancelReason && (
            <div className="text-xs text-red-500 mt-0.5 italic">
              Ausfall: {lesson.cancelReason}
            </div>
          )}
          {isCancelled && !lesson.cancelReason && (
            <div className="text-xs text-red-500 mt-0.5 italic">
              Unterricht entfällt
            </div>
          )}
          {resolutionLabel && (
            <div className="text-xs text-emerald-700 mt-0.5 flex items-center gap-1">
              <span aria-hidden="true">→</span>
              <span className="font-medium">{resolutionLabel}</span>
            </div>
          )}
          {!isCancelled && lesson.notes && (
            <div
              className="text-xs text-muted-foreground mt-0.5 italic truncate"
              title={lesson.notes}
            >
              📝 {lesson.notes}
            </div>
          )}
        </div>

        {/* Action buttons based on lesson state */}
        {!isCancelled && !isOnExcursion && lesson.lesson_id && onOpenDiary && (
          <button
            type="button"
            className="p-1.5 rounded hover:bg-muted/40 text-muted-foreground hover:text-foreground"
            title="Anwesenheit & Tagebuch öffnen"
            onClick={() => {
              const hasStatus =
                !!effectiveStatus && effectiveStatus !== "nicht_eingetragen";
              onOpenDiary(lesson.lesson_id!, hasStatus);
            }}
          >
            <BookOpenText className="h-4 w-4" />
          </button>
        )}

        {!isCancelled && !isOnExcursion && lesson.lesson_id ? (
          isFutureDate ? (
            /* Future lesson: the period hasn't started yet, so no
               attendance can be marked. Show a distinct read-only
               label (not the generic "noch nicht eingetragen") so
               the teacher understands *why* the controls are locked. */
            isNotEntered ? (
              <span className="text-xs px-2 py-1 rounded text-gray-500 bg-gray-50">
                Stunde noch nicht stattgefunden
              </span>
            ) : (
              <span className={`text-xs px-2 py-1 rounded ${color}`}>
                {label}
              </span>
            )
          ) : isNotEntered ? (
            <QuickStatusButtons
              lessonId={lesson.lesson_id}
              changes={changes}
              openLatenessPopover={openLatenessPopover}
              setOpenLatenessPopover={setOpenLatenessPopover}
              setLessonStatus={setLessonStatus}
            />
          ) : (
            <StatusDropdown
              lessonId={lesson.lesson_id}
              label={label}
              color={color}
              isChanged={isChanged}
              isDebugMode={isDebugMode}
              status={lesson.status}
              setLessonStatus={setLessonStatus}
              setOpenLatenessPopover={setOpenLatenessPopover}
              handleDeleteExcursionLessonAttendance={
                handleDeleteExcursionLessonAttendance
              }
            />
          )
        ) : isCancelled ? (
          <CancelledLessonBadge
            lesson={lesson}
            label={label}
            setDeletingAttendanceLessonId={setDeletingAttendanceLessonId}
            setShowDeleteAttendanceConfirm={setShowDeleteAttendanceConfirm}
          />
        ) : isOnExcursion ? (
          <ExcursionLessonBadge
            lesson={lesson}
            label={label}
            tripAttendanceStatus={tripAttendanceStatus}
            savingTripAttendance={savingTripAttendance}
            isFirstExcursionLesson={isFirstExcursionLesson}
            hasErroneousAttendance={hasErroneousAttendance}
            isFutureDate={isFutureDate}
            handleTripAttendanceChange={handleTripAttendanceChange}
            handleDeleteExcursionLessonAttendance={
              handleDeleteExcursionLessonAttendance
            }
          />
        ) : (
          <div className="flex items-center gap-1">
            <span className={`text-xs px-2 py-1 rounded ${color}`}>
              {label}
            </span>
            {isDebugMode && lesson.lesson_id && lesson.status && (
              <button
                type="button"
                onClick={() =>
                  lesson.lesson_id &&
                  handleDeleteExcursionLessonAttendance(lesson.lesson_id)
                }
                className="p-1 rounded hover:bg-red-100 text-red-500 hover:text-red-700"
                title="[DEBUG] Anwesenheitseintrag löschen"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Inline lateness drawer */}
      {openLatenessPopover === lesson.lesson_id && lesson.lesson_id && (
        <LatenessPopover
          lessonId={lesson.lesson_id}
          latenessDetails={latenessDetails}
          setLatenessDetails={setLatenessDetails}
          setLatenessInfo={setLatenessInfo}
          setOpenLatenessPopover={setOpenLatenessPopover}
        />
      )}
    </li>
  );
}
